// src/components/CookAI/Footer/Footer.tsx
import React from "react";
import { ColorTheme } from "../../../themes/colors";
import { useLanguage } from "../../../i18n/LanguageContext";

const APP_STORE_URL = "https://apps.apple.com/us/app/cook-ai-hey-chef/id6758898666";

interface FooterProps {
    theme: ColorTheme;
    onNavigateToLegal?: (page: 'privacy' | 'terms') => void;
}

const Footer: React.FC<FooterProps> = ({ theme, onNavigateToLegal }) => {
    const { t } = useLanguage();

    const handleLegalClick = (page: 'privacy' | 'terms') => {
        if (onNavigateToLegal) {
            onNavigateToLegal(page);
        }
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const linkStyle: React.CSSProperties = {
        color: "rgba(255,255,255,0.60)",
        textDecoration: "none",
        fontSize: "0.92rem",
        transition: "color 0.25s ease",
        background: "none",
        border: "none",
        padding: 0,
        cursor: "pointer",
        font: "inherit",
        fontWeight: 500,
    };

    const hoverIn = (e: React.MouseEvent<HTMLElement>) => {
        (e.currentTarget as HTMLElement).style.color = theme.primary;
    };
    const hoverOut = (e: React.MouseEvent<HTMLElement>) => {
        (e.currentTarget as HTMLElement).style.color = "rgba(255,255,255,0.60)";
    };

    return (
        <footer style={{
            background: "#07091A",
            color: "rgba(255,255,255,0.6)",
            padding: "3.5rem 1.5rem 2rem",
            borderTop: `1px solid rgba(255,255,255,0.06)`,
        }}>
            <div style={{
                maxWidth: "1180px",
                margin: "0 auto",
            }}>
                {/* Top area */}
                <div style={{
                    display: "flex",
                    flexWrap: "wrap",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    gap: "2.5rem",
                    marginBottom: "2.5rem",
                }}>
                    {/* Brand */}
                    <div style={{ maxWidth: "300px" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "0.6rem", marginBottom: "0.9rem" }}>
                            <img
                                src="/images/cook_ai_logo.webp"
                                alt="Cook AI Logo"
                                style={{
                                    width: "42px",
                                    height: "42px",
                                    objectFit: "contain",
                                    borderRadius: "10px",
                                }}
                            />
                            <span style={{
                                fontSize: "1.35rem",
                                fontWeight: 800,
                                fontFamily: "'Bricolage Grotesque', sans-serif",
                                background: theme.primaryGradient,
                                WebkitBackgroundClip: "text",
                                WebkitTextFillColor: "transparent",
                                backgroundClip: "text",
                            }}>
                                Cook AI
                            </span>
                        </div>
                        <p style={{
                            fontSize: "0.9rem",
                            lineHeight: 1.65,
                            color: "rgba(255,255,255,0.5)",
                            marginBottom: "1.25rem",
                        }}>
                            {t.hero.badge} {t.hero.subheadline}
                        </p>

                        {/* App Store badge */}
                        <a
                            href={APP_STORE_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ display: "inline-block", transition: "transform 0.25s ease, opacity 0.25s ease" }}
                            onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-2px)"; }}
                            onMouseLeave={(e) => { e.currentTarget.style.transform = "translateY(0)"; }}
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="148"
                                height="50"
                                viewBox="0 0 160 54"
                                role="img"
                                aria-label="Download on the App Store"
                            >
                                <rect width="160" height="54" rx="10" ry="10" fill="#000" />
                                <rect x="0.5" y="0.5" width="159" height="53" rx="9.5" ry="9.5" fill="none" stroke="rgba(255,255,255,0.35)" strokeWidth="1" />
                                <path
                                    d="M37.3 27.2c0-4.5 3.7-6.7 3.9-6.8-2.1-3.1-5.4-3.5-6.6-3.6-2.8-.3-5.4 1.7-6.8 1.7-1.4 0-3.6-1.6-5.9-1.6-3 .1-5.8 1.8-7.4 4.5-3.2 5.5-.8 13.6 2.2 18.1 1.5 2.2 3.3 4.6 5.6 4.5 2.3-.1 3.1-1.4 5.9-1.4 2.7 0 3.5 1.4 5.9 1.4 2.4 0 4-2.2 5.5-4.4 1.7-2.5 2.4-5 2.5-5.1-.1-.1-4.8-1.8-4.8-7.3z"
                                    fill="#FFF"
                                />
                                <path
                                    d="M32.8 14.4c1.2-1.5 2-3.6 1.8-5.7-1.8.1-3.9 1.2-5.2 2.7-1.1 1.3-2.1 3.4-1.8 5.4 2 .1 4-1 5.2-2.4z"
                                    fill="#FFF"
                                />
                                <text x="52" y="20" fontFamily="-apple-system, BlinkMacSystemFont, 'Helvetica Neue', Arial, sans-serif" fontSize="10" fill="#FFF" letterSpacing="0.3">
                                    Download on the
                                </text>
                                <text x="52" y="38" fontFamily="-apple-system, BlinkMacSystemFont, 'Helvetica Neue', Arial, sans-serif" fontSize="20" fontWeight="600" fill="#FFF" letterSpacing="-0.3">
                                    App Store
                                </text>
                            </svg>
                        </a>
                    </div>

                    {/* Link columns */}
                    <div style={{
                        display: "flex",
                        gap: "4rem",
                        flexWrap: "wrap",
                    }}>
                        <div style={{ display: "flex", flexDirection: "column", gap: "0.8rem" }}>
                            <a href="/" style={linkStyle} onMouseEnter={hoverIn} onMouseLeave={hoverOut}>
                                {t.nav.home}
                            </a>
                            <a href="/#features" style={linkStyle} onMouseEnter={hoverIn} onMouseLeave={hoverOut}>
                                {t.nav.features}
                            </a>
                            <a href="/blog" style={linkStyle} onMouseEnter={hoverIn} onMouseLeave={hoverOut}>
                                {t.nav.blog}
                            </a>
                        </div>
                        <div style={{ display: "flex", flexDirection: "column", gap: "0.8rem" }}>
                            <button
                                onClick={() => handleLegalClick('privacy')}
                                style={linkStyle}
                                onMouseEnter={hoverIn}
                                onMouseLeave={hoverOut}
                            >
                                {t.footer.privacyPolicy}
                            </button>
                            <button
                                onClick={() => handleLegalClick('terms')}
                                style={linkStyle}
                                onMouseEnter={hoverIn}
                                onMouseLeave={hoverOut}
                            >
                                {t.footer.termsOfUse}
                            </button>
                        </div>
                    </div>
                </div>

                {/* Bottom bar */}
                <div style={{
                    borderTop: "1px solid rgba(255,255,255,0.08)",
                    paddingTop: "1.5rem",
                    display: "flex",
                    flexWrap: "wrap",
                    justifyContent: "center",
                    gap: "0.75rem",
                }}>
                    <p style={{ fontSize: "0.85rem", color: "rgba(255,255,255,0.42)", textAlign: "center" }}>
                        {t.footer.copyright}
                    </p>
                </div>
            </div>
        </footer>
    );
};

export default Footer;
